import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Slider, 
  TextField,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import CloseIcon from '@mui/icons-material/Close';
import { Settings } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCalculator } from '../../../contexts/CalculatorContext';
import VoiceTrafficSettingsDialog from './VoiceTraffic/VoiceTrafficSettingsDialog';

const VoiceTraffic = () => {
  const { t } = useTranslation();
  const { 
    voiceTrafficCountriesMinutes = { 'UA': 1000 },
    voiceTrafficUseCustomAmount = false,
    voiceTrafficCustomAmount = 0,
    updateVoiceTrafficCountryMinutes,
    updateVoiceTrafficCustomAmount,
    getVoiceTrafficCountries,
    getVoiceTrafficCustomCurrency,
    voiceTrafficSettingsOpen,
    setVoiceTrafficSettingsOpen,
    hasVoiceTrafficCustomSettings,
    toggleProduct,
    priceCalculation
  } = useCalculator();
  
  // Local state for inputs - separate for each country and custom amount
  const [countryInputs, setCountryInputs] = useState({});
  const [customAmountInput, setCustomAmountInput] = useState(voiceTrafficCustomAmount.toString());

  // Initialize country inputs when countries change
  useEffect(() => {
    const newInputs = {};
    Object.entries(voiceTrafficCountriesMinutes).forEach(([country, minutes]) => {
      newInputs[country] = minutes.toString();
    });
    setCountryInputs(newInputs);
  }, [voiceTrafficCountriesMinutes]);

  // Sync custom amount input
  useEffect(() => {
    setCustomAmountInput(voiceTrafficCustomAmount.toString());
  }, [voiceTrafficCustomAmount]);

  const handleCountryMinutesChange = (country, newValue) => {
    updateVoiceTrafficCountryMinutes(country, newValue);
    setCountryInputs(prev => ({
      ...prev,
      [country]: newValue.toString()
    }));
  };

  const handleCountryInputChange = (country, value) => {
    setCountryInputs(prev => ({
      ...prev,
      [country]: value
    }));
  };

  const handleCountryInputBlur = (country) => {
    const numValue = Number(countryInputs[country]) || 500;
    const correctedValue = Math.max(500, numValue);
    updateVoiceTrafficCountryMinutes(country, correctedValue);
    setCountryInputs(prev => ({
      ...prev,
      [country]: correctedValue.toString()
    }));
  };

  const handleCustomAmountChange = (newValue) => {
    updateVoiceTrafficCustomAmount(newValue);
    setCustomAmountInput(newValue.toString());
  };

  const handleCustomAmountInputChange = (value) => {
    setCustomAmountInput(value);
  };

  const handleCustomAmountInputBlur = () => {
    const numValue = Number(customAmountInput) || 0;
    const correctedValue = Math.max(0, numValue);
    updateVoiceTrafficCustomAmount(correctedValue);
    setCustomAmountInput(correctedValue.toString());
  };

  const handleSettingsClick = () => {
    setVoiceTrafficSettingsOpen(true);
  };

  // Check if there are any custom settings
  const hasAnyCustomSettings = hasVoiceTrafficCustomSettings;

  // Get country name without flag
  const getCountryName = (countryCode) => {
    const names = {
      'UA': t('countries.ukraine', 'Ukraine'),
      'KZ': t('countries.kazakhstan', 'Kazakhstan'),
      'PL': t('countries.poland', 'Poland')
    };
    return names[countryCode] || countryCode;
  };

  // Get slider label text
  const getSliderLabel = (countryCode) => {
    const labels = {
      'UA': t('calculator.voiceTrafficVolumeUA', "Об'єм вихідного зв'язку в Україні"),
      'KZ': t('calculator.voiceTrafficVolumeKZ', "Об'єм вихідного зв'язку в Казахстані"),
      'PL': t('calculator.voiceTrafficVolumePL', "Об'єм вихідного зв'язку в Польщі")
    };
    return labels[countryCode] || `${getCountryName(countryCode)} - ${t('calculator.voiceTrafficMinutesInput', 'Number of minutes per month')}`;
  };

  // Get currency unit text by currency code from package data
  const getCurrencyUnit = (currency) => {
    const units = {
      'UAH': t('calculator.voiceTrafficCurrencyUAH', 'UAH/min'),
      'KZT': t('calculator.voiceTrafficCurrencyKZT', 'KZT/min'),
      'EUR': t('calculator.voiceTrafficCurrencyEUR', '€/min'),
      'PLN': t('calculator.voiceTrafficCurrencyPLN', 'PLN/min'),
    };
    return units[currency] || `${currency}/min`;
  };

  // Build translated package label from structured data
  const buildPackageLabel = (voiceProduct) => {
    const countryName = getCountryName(voiceProduct.countryCode);
    const minutes = voiceProduct.minutes || 0;
    const pricePerMin = voiceProduct.pricePerMinuteLocal || 0;
    const currency = voiceProduct.currency || 'USD';
    const currencyUnit = getCurrencyUnit(currency);
    return `${countryName} ${minutes} ${t('calculator.voiceTrafficMinutesShort', 'min.')} (${pricePerMin} ${currencyUnit})`;
  };

  const renderPricingInfo = () => {
    if (!priceCalculation) return null;

    const summaryItems = priceCalculation.summaryItemsWithDiscounts || priceCalculation.summaryItems;
    if (!summaryItems) return null;

    // Show only package items here (exclude deposit rows) even if deposit is enabled
    const voiceTrafficProducts = summaryItems.filter(item => item.productType === 'voice-traffic' && !item.useCustomAmount);
    if (!voiceTrafficProducts || voiceTrafficProducts.length === 0) return null;

    return (
      <Box sx={{
        mt: 2,
        p: 1.5,
        bgcolor: 'rgba(0, 200, 83, 0.05)',
        borderRadius: 1
      }}>
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {t('calculator.voiceTrafficSelectedPackages', 'Selected packages')}:
          </Typography>

          {/* Display detailed package information for each country */}
          {voiceTrafficProducts.map((voiceProduct, index) => {
            const countryCode = voiceProduct.countryCode;
            const includedMinutes = voiceProduct.minutes || 0;

            // Use Bundle API overage data directly instead of calculating on the fly
            const requestedMinutes = voiceProduct.requestedMinutes || voiceTrafficCountriesMinutes[countryCode] || 0;
            const extraMinutes = voiceProduct.overageMinutes || Math.max(0, requestedMinutes - includedMinutes);
            const hasOverage = extraMinutes > 0;

            return (
              <Box key={`${countryCode}-${index}`} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                {/* Package Info - label built from structured data */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={buildPackageLabel(voiceProduct)}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 152, 0, 0.1)',
                      color: 'warning.main',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      height: '20px'
                    }}
                  />

                  {/* Overage text outside of chip, normal color */}
                  {hasOverage && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      +{extraMinutes.toLocaleString()} {t('calculator.voiceTrafficOverageText', 'min. over package')} ({voiceProduct.overageRateLocal || 0} {getCurrencyUnit(voiceProduct.currency)})
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneInTalkIcon sx={{ color: '#333', fontSize: 20, mr: 1 }} />
            <Typography sx={{ fontWeight: 500 }}>
              {t('calculator.voiceTrafficLabel', 'Voice Traffic')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={t('calculator.voiceTrafficSettings', 'Voice Traffic Settings')}>
              <IconButton
                size="small"
                onClick={handleSettingsClick}
                sx={{ 
                  color: hasAnyCustomSettings ? '#00C853' : '#757575',
                  mr: 1,
                  '&:hover': { color: '#00C853' }
                }}
              >
                <Settings fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <IconButton
              size="small"
              onClick={() => toggleProduct('voice-traffic')}
              sx={{ 
                color: '#333',
                '&:hover': { color: '#00C853' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Deposit slider/input (optional) */}
        {voiceTrafficUseCustomAmount && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              {t('calculator.voiceTrafficCustomAmountInput', 'Custom amount per month')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flex: 1, mr: 2 }}>
                <Slider
                  value={Math.min(voiceTrafficCustomAmount, 1000)}
                  onChange={(event, newValue) => handleCustomAmountChange(newValue)}
                  min={0}
                  max={1000}
                  step={10}
                  valueLabelDisplay="off"
                  sx={{
                    color: '#00C853',
                    '& .MuiSlider-thumb': {
                      width: 16,
                      height: 16,
                      backgroundColor: '#00C853',
                    }
                  }}
                />
              </Box>
              <TextField
                value={customAmountInput}
                onChange={(event) => handleCustomAmountInputChange(event.target.value)}
                onBlur={handleCustomAmountInputBlur}
                type="number"
                variant="outlined"
                size="small"
                sx={{ width: 90 }}
                inputProps={{ inputMode: 'numeric' }}
              />
            </Box>
          </Box>
        )}

        {/* Package Mode - Country Sliders (visible only when packages are enabled) */}
        {(!voiceTrafficUseCustomAmount || hasVoiceTrafficCustomSettings) && (
          <Box sx={{ mt: 2 }}>
            {Object.entries(voiceTrafficCountriesMinutes).map(([country, minutes]) => (
              <Box key={country} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                  {getSliderLabel(country)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Slider
                      value={Math.min(minutes, 50000)}
                      onChange={(event, newValue) => handleCountryMinutesChange(country, newValue)}
                      min={500}
                      max={50000}
                      step={100}
                      valueLabelDisplay="off"
                      sx={{
                        color: '#00C853',
                        '& .MuiSlider-thumb': {
                          width: 16,
                          height: 16,
                          backgroundColor: '#00C853',
                        }
                      }}
                    />
                  </Box>
                  <TextField
                    value={countryInputs[country] || minutes.toString()}
                    onChange={(event) => handleCountryInputChange(country, event.target.value)}
                    onBlur={() => handleCountryInputBlur(country)}
                    type="number"
                    variant="outlined"
                    size="small"
                    sx={{ width: 90 }}
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Pricing Information */}
        {renderPricingInfo()}
      </Box>

      {/* Settings Dialog */}
      <VoiceTrafficSettingsDialog />
    </>
  );
};

export default VoiceTraffic;